import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import CreateIPUnitDeviceCommand from 'features/systems/ip-unit/domain/models/create-ip-unit-device-command';
import CreateIPUnitDeviceResponse from 'features/systems/ip-unit/domain/models/create-ip-unit-device-response';
import { IPUnitConfiguration } from 'features/systems/ip-unit/domain/models/ip-unit-configuration';
import { ReadIPUnitConfigurationResponse } from 'features/systems/ip-unit/domain/models/read-ip-unit-configuration-response';

const baseUrl = '/api/v1/IPUnit';

enum IPUnitQueryPaths {
  UpdateIPUnitConfiguration = 'UpdateIPUnitConfiguration',
  ReadIPUnitConfiguration = 'ReadIPUnitConfiguration',
  CreateIPUnitDevice = 'CreateIPUnitDevice',
}

const updateIPUnitConfiguration = async (axiosInstance: AxiosInstance, ipUnit: IPUnitConfiguration): Promise<IPUnitConfiguration> => {
  const body = JSON.stringify(ipUnit);
  const response = await axiosInstance.put(`${baseUrl}/${IPUnitQueryPaths.UpdateIPUnitConfiguration}`, body);
  return response.data;
};

const readIPUnitConfiguration = async (
  axiosInstance: AxiosInstance,
  systemId: string,
  signal?: AbortSignal,
): Promise<ReadIPUnitConfigurationResponse> => {
  const response = await axiosInstance.get(`${baseUrl}/${IPUnitQueryPaths.ReadIPUnitConfiguration}/${systemId}`, { signal });
  return response.data;
};

const createIPUnitDevice = async (
  axiosInstance: AxiosInstance,
  createIPUnitDeviceCommand: CreateIPUnitDeviceCommand,
): Promise<CreateIPUnitDeviceResponse> => {
  const response = await axiosInstance.post(`${baseUrl}/${IPUnitQueryPaths.CreateIPUnitDevice}`, createIPUnitDeviceCommand);
  return response.data;
};

export const useUpdateIPUnitConfiguration = (): UseMutationResult<IPUnitConfiguration, Error, IPUnitConfiguration, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [IPUnitQueryPaths.UpdateIPUnitConfiguration],
    mutationFn: (ipUnit: IPUnitConfiguration) => updateIPUnitConfiguration(authenticatedInstance, ipUnit),
  });
};

export const useLazyReadIPUnitConfiguration = (systemId: string): UseQueryResult<ReadIPUnitConfigurationResponse, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [IPUnitQueryPaths.ReadIPUnitConfiguration, systemId],
    queryFn: ({ signal }) => readIPUnitConfiguration(authenticatedInstance, systemId, signal),
    enabled: false,
  });
};

export const useCreateIPUnitDevice = (): UseMutationResult<CreateIPUnitDeviceResponse, Error, CreateIPUnitDeviceCommand, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [IPUnitQueryPaths.CreateIPUnitDevice],
    mutationFn: (createIPUnitDeviceCommand: CreateIPUnitDeviceCommand) =>
      createIPUnitDevice(authenticatedInstance, createIPUnitDeviceCommand),
  });
};
